/**
 * "Fotografias e conferência facial", na pré-análise eletrônica (E123.F3, GUS-128, ADR-0079 E.24).
 *
 * A etapa "Análise fotográfica" saiu da esteira: ao entrar o material, o sistema classifica as
 * imagens, elege a fotografia do documento de identidade e a do titular e faz a conferência facial.
 * Esta seção mostra o resultado e é onde o perito revisa: as fotos escolhidas com a origem e a
 * página, quem as escolheu, o parecer da conferência (o mesmo painel de antes) e "Trocar fotos",
 * que abre a seleção e refaz a conferência ao confirmar. As leituras propostas das imagens de
 * documento (E114.F3) ficam aqui também, para o perito confirmar sem abrir a troca. E141.F2
 * (GUS-147, ADR-0079 E.26): "Ajustar recorte" no cartão da foto escolhida abre o editor sobre o
 * original; salvar refaz a conferência, e o cartão aponta o recorte que a leitura automática não fez.
 *
 * Nada aqui decide: o estado vem das leituras do servidor (`estadoDasFotografias`), e a seção relê
 * enquanto a classificação ou a conferência rodam em segundo plano. E124.F1 (GUS-134): a página é
 * avisada quando a análise está em andamento (`onEmAndamento`), para a emissão dizer que o documento
 * baixado agora sairia sem ela.
 */
import { useEffect, useRef, useState } from 'react';

import { Blueprint, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { executarConferenciaFacial } from '@/lib/conferencia_facial';
import { textoDoPendente, type ArquivoPendenteData } from '@/lib/arquivos_pendentes';
import {
  analiseFotograficaEmAndamento,
  eleitaDoGrupo,
  estadoDasFotografias,
  type FotografiasDoCasoData,
} from '@/lib/fotografias_do_caso';
import {
  GRUPOS_DA_FOTO,
  type FotoDoSignatarioData,
  type GrupoDaFoto,
} from '@/lib/fotos_do_signatario';
import {
  useFotografiasDoCaso,
  type FotografiasDoCaso as LeituraDasFotografias,
} from '@/lib/useFotografiasDoCaso';

import { AjustarRecorte } from './AjustarRecorte';
import { LeiturasPendentes } from './AvisosDaLeitura';
import { ParecerDaConferencia } from './ConferenciaFacial';
import { FotoEleitaCartao } from './FotoEleitaCartao';
import { FotografiasDoCasoEstado } from './FotografiasDoCasoEstado';
import { LeiturasDoMaterial } from './LeiturasDoMaterial';
import { RefazerAnaliseMorfologica } from './RefazerAnaliseMorfologica';
import { TrocarFotos } from './TrocarFotos';

const t = ptBR.exameEletronico.fotografias;

/** E191.F1: quando a seção relê de novo depois de uma troca, além da releitura periódica. */
export const RELEITURAS_APOS_A_TROCA_MS: readonly number[] = [4000, 10000, 20000, 40000];

interface CorpoProps {
  caseId: string;
  dados: FotografiasDoCasoData;
  concluido: boolean;
  tentando: boolean;
  onTentarAgora: () => void;
  onLeituraConfirmada: () => void;
  /** E141.F2: abre o ajuste do recorte da foto escolhida. */
  onAjustar: (foto: FotoDoSignatarioData) => void;
  /** E205.F3: na mesa, as leituras dos documentos ficam na aba Documentos. */
  semLeituras: boolean;
  /** E206.F2: o caso aguarda arquivos (a comparação não roda). */
  aguardando: boolean;
}

/** O estado, as fotos escolhidas, o parecer e as leituras — tudo o que vem das leituras do caso. */
function CorpoDasFotografias({
  caseId,
  dados,
  concluido,
  tentando,
  onTentarAgora,
  onLeituraConfirmada,
  onAjustar,
  semLeituras,
  aguardando,
}: CorpoProps): JSX.Element {
  const parecer = dados.conferencia.parecer;
  return (
    <>
      <FotografiasDoCasoEstado
        estado={estadoDasFotografias(dados)}
        concluido={concluido}
        tentando={tentando}
        onTentarAgora={onTentarAgora}
        aguardando={aguardando}
      />
      <div className="grid gap-sc-3 md:grid-cols-2">
        {GRUPOS_DA_FOTO.map((grupo) => (
          <FotoEleitaCartao
            key={grupo}
            caseId={caseId}
            grupo={grupo}
            foto={eleitaDoGrupo(dados.fotos, grupo)}
            candidatas={dados.candidatas}
            concluido={concluido}
            onAjustar={onAjustar}
          />
        ))}
      </div>
      {parecer !== null ? (
        <div className="space-y-sc-2">
          {parecer.conferencia_automatica === true ? (
            <p
              className="text-caption text-fg-dim"
              data-testid="fotografias-conferencia-automatica"
            >
              {t.conferenciaAutomatica}
            </p>
          ) : null}
          <ParecerDaConferencia
            caseId={caseId}
            parecer={parecer}
            parMudou={dados.conferencia.par_mudou}
            recorteMudou={dados.conferencia.recorte_mudou === true}
          />
        </div>
      ) : null}
      {semLeituras ? null : (
        <>
          <LeiturasPendentes leituras={dados.candidatas} />
          <LeiturasDoMaterial
            caseId={caseId}
            candidatas={dados.candidatas}
            concluido={concluido}
            onConfirmada={onLeituraConfirmada}
          />
        </>
      )}
    </>
  );
}

export interface FotografiasDoCasoProps {
  caseId: string;
  /** O perito concluiu alguma peça: o material do exame não muda mais. */
  concluido: boolean;
  /** E124.F1: a análise ainda roda (conferindo, ou organizando sem par); a emissão avisa. */
  onEmAndamento?: (emAndamento: boolean) => void;
  /** E124.F1: muda a cada documento adicionado na página — a seção relê o que ele pediu. */
  releitura?: number;
  /**
   * E167.F1: uma entrada de toda peça mudou aqui — a leitura confirmada, ou a análise que terminou
   * com parecer novo. A página rearma o processamento automático; sem isso, as peças verificadas
   * antes ficavam desatualizadas e a análise saía "corrigir" (GT-2026-0130).
   */
  onEntradaMudou?: () => void;
  /** E205.F3 (ADR-0147 §5): na mesa, "Dados lidos do documento" vai para a aba Documentos. */
  semLeituras?: boolean;
  /**
   * E206.F2 (GUS-296 item 2): o que falta ao caso digital (`arquivos_pendentes` do caso). Com algo
   * pendente, a comparação não roda nem pelo botão: a seção diz o que falta e os botões ficam
   * indisponíveis (o servidor recusa do mesmo jeito).
   */
  arquivosPendentes?: readonly ArquivoPendenteData[] | undefined;
}

function temPendentes(faltam: readonly ArquivoPendenteData[] | undefined): boolean {
  return faltam !== undefined && faltam.length > 0;
}

/** E206.F2 (GUS-296 item 2): o caso aguarda arquivos — a comparação espera, e a seção diz o quê. */
function AguardandoArquivos({
  faltam,
}: {
  faltam: readonly ArquivoPendenteData[] | undefined;
}): JSX.Element | null {
  if (faltam === undefined || faltam.length === 0) return null;
  return (
    <p
      role="status"
      className="border border-warning bg-warning-50 px-sc-3 py-sc-2 text-body-sm text-warning-700"
      data-testid="fotografias-aguardando-arquivos"
    >
      {ptBR.exameEletronico.conferenciaFacial.aguardandoArquivos.replace(
        '{faltam}',
        faltam.map(textoDoPendente).join('; '),
      )}
    </p>
  );
}

/** A seção com a leitura de quem a mostra (E203.F5.T07: a mesa lê as fotografias uma vez só). */
export function FotografiasDoCasoLidas({
  caseId,
  concluido,
  onEmAndamento,
  releitura = 0,
  onEntradaMudou,
  leitura,
  semLeituras = false,
  arquivosPendentes,
}: FotografiasDoCasoProps & { leitura: LeituraDasFotografias }): JSX.Element {
  const client = useApiClient();
  const aguardando = temPendentes(arquivosPendentes);
  const { dados, erro, recarregar } = leitura;
  const emAndamento = analiseFotograficaEmAndamento(dados);
  useEffect(() => onEmAndamento?.(emAndamento), [emAndamento, onEmAndamento]);
  const estavaEmAndamento = useRef(false);
  useEffect(() => {
    if (estavaEmAndamento.current && !emAndamento) onEntradaMudou?.();
    estavaEmAndamento.current = emAndamento;
  }, [emAndamento, onEntradaMudou]);
  // O envio marca a classificação e a leitura em curso antes de responder: relida, a seção passa a
  // esperar por elas (a releitura periódica só existe enquanto algo roda).
  const releituraDaMontagem = useRef(releitura);
  useEffect(() => {
    if (releitura !== releituraDaMontagem.current) void recarregar().catch(() => undefined);
  }, [releitura, recarregar]);
  const [trocando, setTrocando] = useState(false);
  const [ajustando, setAjustando] = useState<FotoDoSignatarioData | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [tentando, setTentando] = useState(false);
  // E191.F1 (GT-2026-0030): a troca em voo é dita no alto da seção, perto das fotos escolhidas.
  const [trocandoGrupo, setTrocandoGrupo] = useState<GrupoDaFoto | null>(null);
  const reler = (): void => void recarregar().catch(() => undefined);
  // E191.F1: a leitura do documento trocado roda em segundo plano e pode cair depois que a
  // conferência já terminou (quando a releitura periódica para) — a seção relê mais algumas vezes.
  const releiturasAgendadas = useRef<number[]>([]);
  useEffect(() => () => releiturasAgendadas.current.forEach((id) => window.clearTimeout(id)), []);
  const relerDepois = (): void => {
    for (const atraso of RELEITURAS_APOS_A_TROCA_MS) {
      releiturasAgendadas.current.push(window.setTimeout(reler, atraso));
    }
  };

  const tentarAgora = (): void => {
    void (async () => {
      setTentando(true);
      try {
        await executarConferenciaFacial(client, caseId);
      } catch {
        // A faixa continua dizendo que a conferência não saiu; a retaguarda tenta de novo.
      } finally {
        setTentando(false);
        reler();
      }
    })();
  };

  return (
    <Blueprint
      as="section"
      className="mt-sc-6 space-y-sc-4 p-sc-6"
      data-testid="fotografias-do-caso"
      data-estado={dados === null ? '' : estadoDasFotografias(dados)}
      aria-labelledby="fotografias-do-caso-titulo"
    >
      <div>
        <h3 id="fotografias-do-caso-titulo" className="text-body-lg text-fg">
          {t.titulo}
        </h3>
      </div>
      {erro ? (
        <p role="alert" className="text-fg-danger text-body-sm" data-testid="fotografias-erro">
          {t.erroCarregar}
        </p>
      ) : null}
      {dados === null ? (
        erro ? null : (
          <p className="text-body-sm text-fg-muted" data-testid="fotografias-carregando">
            {t.carregando}
          </p>
        )
      ) : (
        <>
          {trocandoGrupo !== null ? (
            <p
              role="status"
              aria-live="polite"
              className="border border-border bg-surface-2 px-sc-3 py-sc-2 text-body-sm text-fg"
              data-testid="fotografias-trocando"
              data-grupo={trocandoGrupo}
            >
              {t.trocando(t.grupos[trocandoGrupo])}
            </p>
          ) : null}
          <AguardandoArquivos faltam={arquivosPendentes} />
          <CorpoDasFotografias
            caseId={caseId}
            dados={dados}
            concluido={concluido}
            semLeituras={semLeituras}
            aguardando={aguardando}
            tentando={tentando}
            onTentarAgora={tentarAgora}
            onLeituraConfirmada={() => {
              reler();
              onEntradaMudou?.();
            }}
            onAjustar={(foto) => {
              setAviso(null);
              setAjustando(foto);
            }}
          />
          {ajustando !== null ? (
            <AjustarRecorte
              caseId={caseId}
              foto={ajustando}
              onAjustada={() => {
                setAviso(t.ajustado);
                setAjustando(null);
                reler();
              }}
              onFechar={() => setAjustando(null)}
            />
          ) : null}
          {aviso !== null ? (
            <p
              role="status"
              className="text-body-sm text-success-700"
              data-testid="fotografias-aviso"
            >
              {aviso}
            </p>
          ) : null}
          {/* E205.F5.T01 (ADR-0147 §2.6): as ações da aba numa linha ("Ajustar recorte" fica em
              cada foto, porque é de uma foto só). */}
          <div className="flex flex-wrap items-start gap-sc-2" data-testid="fotografias-acoes">
            <Button
              type="button"
              variant="secondary"
              disabled={concluido}
              onClick={() => setTrocando((aberto) => !aberto)}
              aria-expanded={trocando}
              data-testid="fotografias-trocar"
            >
              {t.trocar}
            </Button>
            {/* E184.F4.C01 (GUS-145 item 5): com parecer, mesmo com documento concluído. */}
            {dados.conferencia.parecer !== null ? (
              <RefazerAnaliseMorfologica
                caseId={caseId}
                emAndamento={emAndamento}
                aguardando={aguardando}
                onRefeita={() => {
                  setAviso(ptBR.exameEletronico.conferenciaFacial.refeita);
                  reler();
                }}
                onEmAndamento={() => {
                  reler();
                  relerDepois();
                }}
              />
            ) : null}
          </div>
          {trocando ? (
            <TrocarFotos
              caseId={caseId}
              concluido={concluido}
              fotos={dados.fotos}
              onTrocada={() => {
                setAviso(t.trocada);
                reler();
                relerDepois();
              }}
              onTrocando={setTrocandoGrupo}
              onFechar={() => setTrocando(false)}
            />
          ) : null}
          {/* ADR-0079 §2.3 e GUS-57: a comparação de imagens não é biometria. E205.F5.T01: a ressalva
              é a nota de rodapé da aba, depois de tudo. */}
          <p
            className="max-w-prose border-t border-border pt-sc-2 text-caption text-fg-dim"
            data-testid="fotografias-regua-etica"
          >
            {ptBR.exameEletronico.conferenciaFacial.reguaEtica}
          </p>
        </>
      )}
    </Blueprint>
  );
}

export function FotografiasDoCaso(props: FotografiasDoCasoProps): JSX.Element {
  const leitura = useFotografiasDoCaso(useApiClient(), props.caseId);
  return <FotografiasDoCasoLidas {...props} leitura={leitura} />;
}
